/**
 * Hook to track wallet connection metrics.
 * Integrates with wagmi to automatically track connection attempts.
 */
import type { AppKitSSRHandle } from '@repo/web3';
import { type Config, type Connector, watchConnectors } from '@wagmi/core';
import { useEffect, useRef } from 'react';
import type { UseAccountReturnType } from 'wagmi';
import type { WalletConnectionStatus } from '@/app/api/t/constants';
import { createWalletConnectionTracker, normalizeProviderName, trackWalletConnection } from '../trackers/wallet-connection';
import type { WalletType } from '../../shared/wallet-type';

/**
 * How long a `connecting → disconnected` drop waits for AppKit to report why.
 * wagmi resets the status before `connect()` rejects, and AppKit only emits
 * `USER_REJECTED` / `CONNECT_ERROR` once the rejection has propagated.
 */
export const CONNECT_ERROR_GRACE_MS = 1_000;

/**
 * How long a connection still pending after the modal closed may take before it
 * counts as `user_rejected`. Closing the modal doesn't cancel an extension or
 * mobile approval, and a WalletConnect pairing never settles on its own.
 */
export const DISMISSED_SETTLE_MS = 60_000;

/**
 * Attempts older than this are dropped without a beacon. Some never settle (a
 * switch outside the modal that fails silently, a wallet type that never
 * resolves), and a later unrelated event must not close them.
 */
export const MAX_ATTEMPT_MS = 5 * 60_000;
interface ConnectionAttempt {
  connectorUid: string;
  /** wagmi's connector when the attempt opened, which a failed switch falls back to. */
  previousUid: string | null;
  provider: string;
  startTime: number;
  /** The modal closed before a wallet connected. */
  dismissed: boolean;
  /** wagmi left `connecting` without this connector; the grace timer is running. */
  dropped: boolean;
  /** Set once wagmi connected this connector; only the wallet type may still be resolving. */
  connected?: {
    chainId?: number;
  };
}
type AttemptEnd = {
  status: Exclude<WalletConnectionStatus, 'success'>;
  error?: unknown;
};
interface UseWalletConnectionTrackerParams {
  account: UseAccountReturnType;
  walletType?: WalletType;
  config: Config;
  modal: Pick<AppKitSSRHandle, 'subscribeEvents'>;
}

/**
 * React hook to automatically track wallet connection metrics and events.
 *
 * Place this hook high in your component tree (e.g., inside a Web3Provider)
 * to monitor and record wallet connection attempts, their durations,
 * success/failure outcomes, and encountered connection errors.
 *
 * An attempt opens on the connector's `connecting` message, which only
 * wagmi's `connect()` emits. `reconnect()` also sets the status to
 * `connecting` when nothing is stored, so AppKit's startup sync and
 * `WagmiProvider`'s mount reconnect cycle `disconnected → connecting →
 * disconnected` on every page load for visitors without a wallet; the status
 * alone can't tell those apart from a user picking a wallet. The Safe
 * connector is skipped: the Safe iframe connects it on every page load.
 *
 * The attempt ends on `connected` (success), on AppKit's `USER_REJECTED` /
 * `CONNECT_ERROR` events (carrying the connector's error). A drop back to
 * `disconnected` with no AppKit event (a `connect()` outside the modal) is
 * logged after {@link CONNECT_ERROR_GRACE_MS}: `user_rejected` if the modal was
 * closed first, otherwise a failure without an error. A connection still
 * pending {@link DISMISSED_SETTLE_MS} after the modal closed is `user_rejected`.
 */
export function useWalletConnectionTracker({
  account,
  walletType,
  config,
  modal
}: UseWalletConnectionTrackerParams) {
  const connectionAttempt = useRef<ConnectionAttempt | null>(null);
  const pendingFailure = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const walletTypeRef = useRef(walletType);
  walletTypeRef.current = walletType;
  const configRef = useRef(config);
  configRef.current = config;

  /** The open attempt, or null once it has outlived {@link MAX_ATTEMPT_MS}. */
  const openAttempt = useRef(() => {
    const attempt = connectionAttempt.current;
    if (attempt && performance.now() - attempt.startTime > MAX_ATTEMPT_MS) {
      clearTimeout(pendingFailure.current);
      connectionAttempt.current = null;
    }
    return connectionAttempt.current;
  }).current;
  const endAttempt = useRef(({
    status,
    error
  }: AttemptEnd) => {
    const attempt_0 = openAttempt();
    if (!attempt_0) return;
    clearTimeout(pendingFailure.current);
    connectionAttempt.current = null;
    trackWalletConnection({
      provider: attempt_0.provider,
      status,
      durationSeconds: (performance.now() - attempt_0.startTime) / 1000,
      error,
      walletType: walletTypeRef.current
    });
  }).current;
  const succeedAttempt = useRef((chainId: number | undefined) => {
    const attempt_1 = openAttempt();
    if (!attempt_1) return;
    clearTimeout(pendingFailure.current);
    connectionAttempt.current = null;
    trackWalletConnection({
      provider: attempt_1.provider,
      status: 'success',
      durationSeconds: (performance.now() - attempt_1.startTime) / 1000,
      chainId,
      walletType: walletTypeRef.current
    });
  }).current;

  /** Ends an attempt that settled with no AppKit event to explain it. */
  const settleWithoutEvent = useRef(() => {
    const attempt_2 = openAttempt();
    if (!attempt_2) return;
    // `account` is screened and can read disconnected while AppKit's address
    // catches up; wagmi's own state says whether this connector made it.
    const {
      status: status_0,
      current
    } = configRef.current.state;
    if (status_0 === 'connected' && current === attempt_2.connectorUid) return;
    // It connected, then went away before its wallet type resolved.
    if (attempt_2.connected) return succeedAttempt(attempt_2.connected.chainId);
    endAttempt({
      status: attempt_2.dismissed ? 'user_rejected' : 'failure'
    });
  }).current;

  // Open an attempt on each explicit connect(), with the connector that was picked.
  useEffect(() => {
    const unsubscribers = new Map<string, () => void>();
    const subscribe = (connectors: readonly Connector[]) => {
      const uids = new Set(connectors.map(connector => connector.uid));
      unsubscribers.forEach((unsubscribe, uid) => {
        if (uids.has(uid)) return;
        unsubscribe();
        unsubscribers.delete(uid);
      });
      for (const connector_0 of connectors) {
        if (unsubscribers.has(connector_0.uid) || connector_0.type === 'safe') continue;
        const onMessage = ({
          type
        }: {
          type: string;
        }) => {
          if (type !== 'connecting') return;
          // A new connect() settles the previous attempt first: one that already
          // dropped keeps its outcome, one still pending was abandoned.
          const pending = openAttempt();
          const {
            current: current_0,
            connections
          } = config.state;
          if (pending && current_0 === pending.connectorUid) {
            // Already connected, with only its wallet type still resolving.
            succeedAttempt(connections.get(current_0)?.chainId);
          } else if (pending?.dropped) settleWithoutEvent();else if (pending) endAttempt({
            status: 'user_rejected'
          });
          clearTimeout(pendingFailure.current);
          connectionAttempt.current = {
            connectorUid: connector_0.uid,
            previousUid: config.state.current,
            provider: normalizeProviderName(connector_0.id, connector_0.name),
            startTime: performance.now(),
            dismissed: false,
            dropped: false
          };
        };
        connector_0.emitter.on('message', onMessage);
        unsubscribers.set(connector_0.uid, () => connector_0.emitter.off('message', onMessage));
      }
    };
    subscribe(config.connectors);
    const unwatch = watchConnectors(config, {
      onChange: subscribe
    });
    return () => {
      unwatch();
      unsubscribers.forEach(unsubscribe_0 => unsubscribe_0());
    };
  }, [config, openAttempt, endAttempt, succeedAttempt, settleWithoutEvent]);

  // Settle on wagmi's own state, not the screened `account`: screening can hold
  // `account` on disconnected while wagmi connects, then tear the wallet down.
  // An attempt whose connector is neither connecting nor connected dropped:
  // back to disconnected, or back to the previous wallet after a failed switch.
  // Give AppKit time to report why.
  useEffect(() => config.subscribe(state => `${state.status}:${state.current ?? ''}`, () => {
    const attempt_3 = openAttempt();
    if (!attempt_3) return;
    const {
      status: status_1,
      current: current_1
    } = config.state;
    if (status_1 === 'connecting') return;
    // Only the attempt's connector, or the wallet a failed switch falls back
    // to, settles it. An abandoned connector connecting late does not.
    if (status_1 === 'connected' && current_1 === attempt_3.connectorUid) {
      attempt_3.connected = {
        chainId: config.state.connections.get(current_1)?.chainId
      };
      return;
    }
    if (status_1 === 'connected' && current_1 !== attempt_3.previousUid) return;
    attempt_3.dropped = true;
    clearTimeout(pendingFailure.current);
    pendingFailure.current = setTimeout(settleWithoutEvent, CONNECT_ERROR_GRACE_MS);
  }), [config, openAttempt, settleWithoutEvent]);

  // AppKit reports why a connect() from its modal failed, and when the modal is dismissed.
  useEffect(() => modal.subscribeEvents(({
    data
  }) => {
    const attempt_4 = openAttempt();
    if (!attempt_4 || data.type !== 'track') return;
    switch (data.event) {
      case 'USER_REJECTED':
        endAttempt({
          status: 'user_rejected',
          error: new Error(data.properties.message)
        });
        break;
      case 'CONNECT_ERROR':
        endAttempt({
          status: 'failure',
          error: new Error(data.properties.message)
        });
        break;
      case 'MODAL_CLOSE':
        {
          // Only a connection by the attempted connector makes the close a
          // success. AppKit's `connected` flag also reads true while switching
          // away from a wallet that is still connected.
          const {
            status: status_2,
            current: current_2
          } = config.state;
          if (status_2 === 'connected' && current_2 === attempt_4.connectorUid) break;
          attempt_4.dismissed = true;
          // A dropped attempt already has its grace timer running.
          if (!attempt_4.dropped) {
            clearTimeout(pendingFailure.current);
            pendingFailure.current = setTimeout(settleWithoutEvent, DISMISSED_SETTLE_MS);
          }
          break;
        }
    }
  }), [modal, config, openAttempt, endAttempt, settleWithoutEvent]);
  useEffect(() => {
    const attempt_5 = openAttempt();

    // Detect successful connection. If wallet type is not known yet (for example
    // bytecode is still loading for smart-wallet detection), keep the attempt
    // open and emit once the type becomes available on a later render. A failed
    // switch to another wallet also lands on `connected`, with the old connector.
    if (account.status === 'connected' && attempt_5 && account.connector?.uid === attempt_5.connectorUid && walletType != null) {
      succeedAttempt(account.chainId);
    }
  }, [account.status, account.connector, account.chainId, walletType, openAttempt, succeedAttempt]);
  useEffect(() => () => clearTimeout(pendingFailure.current), []);

  // Return a manual tracker for cases where automatic tracking isn't sufficient
  return {
    /**
     * Manually create a tracker for connection attempts.
     * Useful when you need more control over tracking.
     */
    createTracker: (provider: string, chainId_0?: number) => createWalletConnectionTracker({
      provider,
      chainId: chainId_0,
      walletType
    }),
    /**
     * Manually track a connection event.
     */
    track: trackWalletConnection
  };
}